import type { ChangeEvent } from 'react';
import { useCallback, useEffect } from 'react';

import type { SKUFormSchema } from '@/_pages/settings/inventory-kits/shared/schemas/skus';
import { useBoolean } from '@/_shared';

import type { SKUsProps } from './shared';
import { useFormDefinition } from './shared';

export type Props = Omit<SKUsProps, 'value' | 'onChange'> & {
  value: SKUFormSchema;
  onChange: (v: SKUFormSchema) => void;
};

export function useUpdateSKUItem({ onChange, value }: Props) {
  const [isPending, setIsPending] = useBoolean();
  const { id, pivot } = value;
  const form = useFormDefinition({ ...value });
  const [localIntent, localPrimary] = form.watch(['intent', 'pivot.primary']);

  const validateForm = useCallback(async () => {
    const newV = form.getValues();
    const res = await form.trigger();
    if (!res) {
      // Return undefined to differentiate between API error and form validation error
      return undefined;
    }
    return newV;
  }, [form]);

  const onSubmit = useCallback(async () => {
    setIsPending.setTrue();
    const newV = await validateForm();

    if (newV) {
      if (newV.intent === 'create') {
        onChange({ ...newV });
      } else {
        onChange({ ...newV, intent: form.formState.defaultValues?.sku === newV.sku ? undefined : 'update' });
      }
    }
    setIsPending.setFalse();
    return Boolean(newV);
  }, [form.formState.defaultValues?.sku, onChange, setIsPending, validateForm]);

  const handleOnChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      form.setValue('sku', target.value, { shouldDirty: true });
      onChange({ ...form.getValues(), sku: target.value });
    },
    [form, onChange]
  );

  useEffect(() => {
    const { setDirtyHelper, intent } = form.getValues();
    if (!setDirtyHelper && intent === 'create') {
      form.setValue('setDirtyHelper', true, { shouldDirty: true });
    }

    form.setValue('pivot.primary', pivot.primary, { shouldDirty: true });
  }, [form, onChange, pivot.primary]);

  useEffect(() => {
    if (localPrimary === 1) {
      onChange(form.getValues());
    }
    if (localIntent === 'delete') {
      onChange(form.getValues());
    }
  }, [form, localIntent, localPrimary, onChange]);

  return { id, pivot, form, onSubmit, handleOnChange, isPending };
}
